import type { Dispatch, UnknownAction } from '@reduxjs/toolkit';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import identity from 'lodash/identity';
import omit from 'lodash/omit';
import { createAction as reduxActionsCreator } from 'redux-actions';

import type { UndoProps } from '../undo/undoTypes';
import type { BaseResource, CoreAction, QcsSyncResourcesConfig, SagaCallback } from './resourceStoreTypes';

/** What dispatch sites pass in `meta.props`; actions whose handlers read a key declare it. */
export type MetaProps = { readonly [key: string]: unknown };

export type Meta<T extends BaseResource = BaseResource, R = unknown, Props = MetaProps> = {
  context?: string;
  // Method syntax: a dispatcher's promise callbacks may expect the result its saga resolves with.
  resolve?(result: R): void;
  reject?(error: unknown): void;
  undo?: UndoProps;
  response?: AxiosResponse<unknown>;
  props?: Props;
  config?: QcsSyncResourcesConfig<T>;
  axiosConfig?: AxiosRequestConfig;
  scope?: string;
  skipPersistDebounce?: boolean;
};
// A helper to use with redux toolkit's createAction API to make an action creator that accepts payload and meta arguments.
export function getPayloadCreator<P = void, T extends BaseResource = BaseResource, M = Meta<T>>() {
  return (payload: P, meta?: M) => ({ payload, meta });
}

// Failure counterpart that keeps redux-actions' FSA behavior: Error payloads set `error`, and payloads are optional.
export function getFailurePayloadCreator<P = unknown, M = Meta>() {
  return (payload?: P, meta?: M) => ({
    payload,
    meta,
    ...(payload instanceof Error && { error: true as const }),
  });
}

export type OptionalLegacyActionCreator<Payload, Metadata = Meta> = {
  (payload?: Payload, metadata?: Metadata): CoreAction<Payload, Metadata | undefined>;
  toString(): string;
};

export type RequiredLegacyActionCreator<Payload, Metadata = Meta> = {
  (payload: Payload, metadata?: Metadata): CoreAction<Payload, Metadata | undefined>;
  toString(): string;
};

type LegacyPayloadCreator<Payload> = (payload: Payload) => Payload;
type LegacyMetaCreator<Payload, Metadata> = (payload: Payload, metadata?: Metadata) => Metadata | undefined;

const identityPayloadCreator = <Payload>(payload: Payload) => identity(payload);

// enhances the default create action to pass along meta data from 2nd argument when using action
// TODO: may want to deprecate this in favor of redux createAction
export function createAction<Payload = unknown, Metadata = Meta>(
  type: string,
): OptionalLegacyActionCreator<Payload, Metadata>;
export function createAction<Payload, Metadata = Meta>(
  type: string,
  payloadCreator: LegacyPayloadCreator<Payload>,
  metaCreator?: LegacyMetaCreator<Payload, Metadata>,
): RequiredLegacyActionCreator<Payload, Metadata>;
export function createAction<Payload, Metadata = Meta>(
  type: string,
  payloadCreator?: LegacyPayloadCreator<Payload>,
  metaCreator?: LegacyMetaCreator<Payload, Metadata>,
): OptionalLegacyActionCreator<Payload, Metadata> | RequiredLegacyActionCreator<Payload, Metadata> {
  return reduxActionsCreator(
    type,
    payloadCreator ?? identityPayloadCreator<Payload>,
    metaCreator ?? ((_payload: Payload, meta?: Metadata) => meta),
  );
}

/**
 * Binds a creator to dispatch; the promise settles with what the saga passes the meta's callbacks (`R`).
 * The creator compares bivariantly, so a caller may state its payload and meta more broadly.
 */
export const bindPromiseAction =
  <P = unknown, M = undefined, R = void>(
    dispatch: Dispatch,
    actionCreator: SagaCallback<[payload: P, meta?: M], UnknownAction>,
  ) =>
  (payload: P, meta?: M): Promise<R> =>
    new Promise<R>((resolve, reject) => dispatch(actionCreator(payload, Object.assign({}, meta, { resolve, reject }))));

/** Copies an action's meta for the actions that report its outcome, leaving out its promise callbacks. */
export function cpMetaForResponseAction<M extends object>(meta: M): Omit<M, 'resolve' | 'reject'>;
export function cpMetaForResponseAction<M extends object>(
  meta: M | null | undefined,
): Omit<M, 'resolve' | 'reject'> | null;
export function cpMetaForResponseAction<M extends object>(meta: M | null | undefined) {
  return meta ? omit(meta, 'resolve', 'reject') : null;
}

/** Copies an action's meta for an action it embeds, leaving out its promise callbacks. */
export function cpMetaForEmbeddedAction<M extends object>(meta: M): Omit<M, 'resolve' | 'reject'>;
export function cpMetaForEmbeddedAction<M extends object>(
  meta: M | null | undefined,
): Omit<M, 'resolve' | 'reject'> | null;
export function cpMetaForEmbeddedAction<M extends object>(meta: M | null | undefined) {
  return meta ? omit(meta, 'resolve', 'reject') : null;
}

// Failure actions carry whatever the request threw.
export const errorPayloadCreator = getPayloadCreator<unknown>();
